import React from "react";
import { motion } from "framer-motion";
import { CheckIcon, ClockIcon, LinkIcon, FileTextIcon, ArrowRightIcon, ShieldCheckIcon } from "lucide-react";
import { ELM, MAIN, AGENT, BRAND } from "../lib/data";
import { FacebookLogo, InstagramLogo, GoogleLogo } from "./PlatformLogos";
import { ClickHint } from "./ClickHint";
import { Logo } from "./Logo";
const STEPS = [{
  label: "Order Received",
  state: "done"
}, {
  label: "Ads Designed",
  state: "done"
}, {
  label: "In Review",
  state: "current",
  eta: "ETA: Thursday 2:00 PM"
}, {
  label: "Live on Facebook, Instagram & Google",
  state: "upcoming"
}, {
  label: "Seller Report Delivered",
  state: "upcoming"
}] as const;
const ADS = [{
  src: "https://cdn.magicpatterns.com/patterns/generated-images/4eb851a1-a057-496c-aba6-4cc474cfb1d9.jpg",
  label: "Just Listed — Social"
}, {
  src: "https://cdn.magicpatterns.com/patterns/generated-images/91aa69a2-cda6-47af-8aa7-2491a2a0cdda.jpg",
  label: "Open House Announcement"
}, {
  src: "https://cdn.magicpatterns.com/patterns/generated-images/dc89f424-70f5-404d-9456-7e8196945870.jpg",
  label: "Property Feature"
}];
export function OrderTracker({
  onBack,
  onHome
}: {
  onBack: () => void;
  onHome: () => void;
}) {
  const currentIndex = STEPS.findIndex(s => s.state === "current");
  const progressPct = currentIndex / (STEPS.length - 1) * 100;
  return <div className="min-h-full w-full bg-neutral-50 text-neutral-900" data-id="element-507" data-mp="175tc7tgw63fd">
      {/* Public header (no login) */}
      <header className="border-b border-neutral-200 bg-white" data-id="element-508" data-mp="1byw662pkzew3">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3.5 sm:px-8" data-id="element-509" data-mp="y6prkl22tfyr">
          <Logo className="h-6 w-auto sm:h-7" data-id="element-510" data-mp="134838j7ha9ag" />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-500" data-id="element-511" data-mp="1359ikzvj1hxz">
            <ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-512" data-mp="t7d5w3mxyexr" /> Live tracker · no login needed
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-8 sm:px-8" data-id="element-513" data-mp="a309jvx8759z">
        {/* Back affordance (demo only) */}
        <button onClick={onBack} className="mb-4 text-sm font-medium text-neutral-500 transition hover:text-neutral-900" data-id="element-514" data-mp="5aqe60dcdd0b">
          ← Back to My Orders
        </button>

        {/* Listing hero */}
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-id="element-515" data-mp="3rglwsqj7qg2t">
          <div className="relative h-52 w-full sm:h-64" data-id="element-516" data-mp="t0b4j4btrcjm">
            <img src={ELM.hero} alt={ELM.address} className="h-full w-full object-cover" data-id="element-517" data-mp="o1q6nbk1p4yc" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-518" data-mp="j2dh2j1shjs2" />
            <div className="absolute bottom-0 left-0 p-5 sm:p-6" data-id="element-519" data-mp="e4brdf9uym7m">
              <p className="text-sm font-medium text-white/80" data-id="element-520" data-mp="e66birdb2aan">Marketing for</p>
              <h1 className="font-heading text-2xl font-bold text-white sm:text-3xl" data-id="element-521" data-mp="33ufd370ffbwr">{ELM.address}</h1>
              <p className="mt-0.5 text-sm text-white/90" data-id="element-522" data-mp="2ysq0hmxk2q6k">
                {ELM.type} · {ELM.price}
              </p>
            </div>
          </div>

          {/* Progress tracker */}
          <div className="p-6 sm:p-8" data-id="element-523" data-mp="1jbmobr6dpvah">
            <div className="relative" data-id="element-524" data-mp="1oa6fc5ztxx9p">
              {/* Track line */}
              <div className="absolute left-0 right-0 top-4 hidden h-1 rounded-full bg-neutral-200 sm:block" data-id="element-525" data-mp="1o8v6hrrj1zbq" />
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progressPct}%`
            }} transition={{
              duration: 0.9,
              ease: "easeOut"
            }} className="absolute left-0 top-4 hidden h-1 rounded-full sm:block" style={{
              backgroundColor: BRAND.accent
            }} data-id="element-526" data-mp="1t2pqyxeyiibv" />

              <ol className="relative grid gap-6 sm:grid-cols-5 sm:gap-2" data-id="element-527" data-mp="2c00qmj4i3dru">
                {STEPS.map((step, i) => {
                const isDone = step.state === "done";
                const isCurrent = step.state === "current";
                return <li key={step.label} className="flex items-start gap-3 sm:flex-col sm:items-center sm:text-center" data-id="element-528" data-mp="282uzg29bqzw5" data-mpi={i}>
                      <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${isDone ? "text-white" : isCurrent ? "text-white ring-4" : "border border-neutral-300 bg-white text-neutral-400"}`} style={isDone || isCurrent ? {
                    backgroundColor: BRAND.accent,
                    ...(isCurrent ? {
                      boxShadow: `0 0 0 4px ${BRAND.accentSoft}`
                    } : {})
                  } : undefined} data-id="element-529" data-mp="2349yu1q5ue76">
                        {isDone ? <CheckIcon className="h-4 w-4" data-id="element-530" data-mp="15mjjlwwq2dcw" /> : isCurrent ? <ClockIcon className="h-4 w-4" data-id="element-531" data-mp="11nauj5cmn3no" /> : i + 1}
                      </span>
                      <div className="sm:mt-2" data-id="element-532" data-mp="riawcu5l4w5b">
                        <p className={`text-sm font-semibold leading-snug ${isCurrent ? "text-neutral-900" : isDone ? "text-neutral-700" : "text-neutral-400"}`} data-id="element-533" data-mp="rhjbad9m6gta">
                          {step.label}
                        </p>
                        {isCurrent && "eta" in step && <span className="mt-1.5 inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold" style={{
                      backgroundColor: BRAND.accentSoft,
                      color: BRAND.accentText
                    }} data-id="element-534" data-mp="1a9vbatcsp609">
                            {step.eta}
                          </span>}
                      </div>
                    </li>;
              })}
              </ol>
            </div>
          </div>
        </div>

        {/* Ad creatives */}
        <section className="mt-8" data-id="element-535" data-mp="1whhilp3y8xmg">
          <h2 className="text-lg font-bold tracking-tight text-neutral-900" data-id="element-536" data-mp="1wgfw8ohhgopf">Your ad creatives</h2>
          <p className="text-sm text-neutral-500" data-id="element-537" data-mp="1rjz8hve74ccv">Designed for {ELM.address} — currently in final review.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-538" data-mp="1mqd5kuxu3mu9">
            {ADS.map(ad => <figure key={ad.label} className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-id="element-539" data-mp="1hs1xaon6tke5">
                <img src={ad.src} alt={ad.label} className="aspect-square w-full object-cover" data-id="element-540" data-mp="1cvbdbq89i4ng" />
                <figcaption className="px-3.5 py-2.5 text-xs font-medium text-neutral-600" data-id="element-541" data-mp="18qlmwgwdhi9x">{ad.label}</figcaption>
              </figure>)}
          </div>
        </section>

        {/* Where ads appear */}
        <section className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6" data-id="element-542" data-mp="yw3g32vnt6lw">
          <h2 className="text-lg font-bold tracking-tight text-neutral-900" data-id="element-543" data-mp="2y6bjpd7vcz2c">Where your ads will appear</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-544" data-mp="2t5xahruj7j2q">
            {[{
            name: "Facebook",
            sub: "Feed & Marketplace",
            logo: <FacebookLogo data-id="element-545" data-mp="35qcexpk67h4h" />
          }, {
            name: "Instagram",
            sub: "Feed & Stories",
            logo: <InstagramLogo data-id="element-546" data-mp="35o9gch4ycfo5" />
          }, {
            name: "Google",
            sub: "Search & Display",
            logo: <GoogleLogo data-id="element-547" data-mp="3ai43xvnohesi" />
          }].map(p => <div key={p.name} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3" data-id="element-548" data-mp="3fgozhhrzjaam">
                {p.logo}
                <div data-id="element-549" data-mp="2hv1ctolc06ca">
                  <p className="text-sm font-semibold text-neutral-900" data-id="element-550" data-mp="2mri2in86isrh">{p.name}</p>
                  <p className="text-xs text-neutral-500" data-id="element-551" data-mp="2rqum8z3r7jpu">{p.sub}</p>
                </div>
              </div>)}
          </div>
        </section>

        {/* Buttons */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-552" data-mp="2wnusba0lsmgy">
          <motion.button whileTap={{
          scale: 0.97
        }} transition={{
          type: "spring",
          stiffness: 400,
          damping: 22
        }} className="inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-5 py-3 text-sm font-semibold text-neutral-800 transition hover:bg-neutral-50" data-id="element-553" data-mp="6zu3mm9luq61">
            <LinkIcon className="h-4 w-4" data-id="element-554" data-mp="s2tjax3ig11o" /> Resend my links
          </motion.button>
          <motion.button whileTap={{
          scale: 0.97
        }} transition={{
          type: "spring",
          stiffness: 400,
          damping: 22
        }} className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90" style={{
          backgroundColor: BRAND.accent
        }} data-id="element-555" data-mp="16vn9pdkbm1l4">
            <FileTextIcon className="h-4 w-4" data-id="element-556" data-mp="1prdef8jn1c2p" /> View Seller Report
          </motion.button>
        </div>

        {/* Closing nudge */}
        <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border p-5 sm:flex-row sm:items-center" style={{
        backgroundColor: BRAND.accentSoft,
        borderColor: BRAND.accent + "33"
      }} data-id="element-557" data-mp="1ku4rkunjojv4">
          <div className="flex items-center gap-4" data-id="element-558" data-mp="222xklvrl8gg3">
            <img src={MAIN.hero} alt={MAIN.address} className="h-14 w-14 rounded-xl object-cover" data-id="element-559" data-mp="223fs5ojpah3b" />
            <div data-id="element-560" data-mp="270z5mm8kqpu7">
              <p className="text-[15px] font-semibold text-neutral-900" data-id="element-561" data-mp="273ldyo3ah9dh">
                You also have {MAIN.address} listed with no marketing in progress.
              </p>
              <p className="text-sm text-neutral-600" data-id="element-562" data-mp="1xactj202ghg9">Start it in one click.</p>
            </div>
          </div>
          <ClickHint onClick={onHome} badge={false} rounded="rounded-xl" className="shrink-0" data-id="element-563" data-mp="1je96ptrivbdt">
            <span className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90" style={{
            backgroundColor: BRAND.accent
          }} data-id="element-564" data-mp="1ockd1uilz3ra">
              Start now <ArrowRightIcon className="h-4 w-4" data-id="element-565" data-mp="33rhwvzm78grv" />
            </span>
          </ClickHint>
        </div>

        <p className="mt-8 text-center text-xs text-neutral-400" data-id="element-566" data-mp="38rmdrqr0rdim">
          Powered by {AGENT.brokerage} · This is a shareable tracking link
        </p>
      </main>
    </div>;
}